import { ThreeEngine3D } from '@/infrastructure/engine3d/ThreeEngine3D';
import { pickProduct3DStrategy } from '@/infrastructure/engine3d/strategies/pickProduct3DStrategy';
import type { I3DEngine } from '@/application/ports/I3DEngine';
import type { ICameraController } from '@/application/ports/ICameraController';
import type { IFoldableStrategy } from '@/application/ports/IFoldableStrategy';
import type { IProduct3DStrategy } from '@/infrastructure/engine3d/strategies/IProduct3DStrategy';
import type { IViewportEngine3D } from '@/application/ports/IViewportEngine3D';
import type { TProductMeshParams } from '@/application/ports/TProductMeshParams';

// Composition root for the Three 3D adapters. Mirrors createFabricAdapters
// on the 2D side: instantiates the concrete engine + strategy behind
// their port typings and returns the ready-promise the composable
// awaits before letting Vue interact with the controller. The
// application-layer Preview3DController is built by the composable
// itself — this factory stays in infra and knows nothing about it.

type TCreateThreeAdaptersParams = {
    canvas: HTMLCanvasElement;
    container: HTMLElement;
    isMobile?: boolean;
    meshParams: TProductMeshParams;
};

type TThreeAdapters = {
    cameraController: ICameraController;
    engine: I3DEngine;
    foldable?: IFoldableStrategy;
    ready: Promise<void>;
    strategy: IProduct3DStrategy;
    viewport: IViewportEngine3D;
};

// ThreeEngine3D implements both I3DEngine and IViewportEngine3D — the
// factory exposes each port under its own field so Preview3DController
// can receive them separately (documents which methods it consumes
// under which contract, no cast).
function createThreeAdapters(params: TCreateThreeAdaptersParams): TThreeAdapters {
    const engine = new ThreeEngine3D({
        canvas: params.canvas,
        container: params.container,
        isMobile: params.isMobile,
    });
    const { foldable, strategy } = pickProduct3DStrategy(params.meshParams, {
        getMaxAnisotropy: () => engine.getMaxAnisotropy(),
    });
    const ready = engine.attachStrategy(strategy).catch((err: unknown) => {
        console.error('createThreeAdapters: strategy attach failed', err);
    });
    return { cameraController: engine.getCameraController(), engine, foldable, ready, strategy, viewport: engine };
}

export { createThreeAdapters };
export type { TCreateThreeAdaptersParams, TThreeAdapters };
